import { dequal } from 'dequal';
import { useCallback, useId, useRef, useSyncExternalStore } from 'react';
import { getSettleState, subscribeSettleState } from '@/common/queries/itemsTreeSetSettleState';

export type SettleMark = 'pending' | 'settled';

const EMPTY_SETTLE_MARKS: ReadonlyMap<number, SettleMark> = new Map();

export function useItemSettleMarks(
  renderedItemIds: { has: (id: number) => boolean },
  enabled: boolean,
): ReadonlyMap<number, SettleMark> {
  const scope = useId();
  const subscribe = useCallback(
    (listener: () => void) => (enabled ? subscribeSettleState(scope, listener) : () => {}),
    [scope, enabled],
  );
  const prevMarksRef = useRef<ReadonlyMap<number, SettleMark>>(EMPTY_SETTLE_MARKS);
  const getSnapshot = useCallback(() => {
    if (!enabled) return EMPTY_SETTLE_MARKS;

    const { pendingIds, settledIds } = getSettleState(scope);
    const marks = new Map<number, SettleMark>();
    for (const id of settledIds) if (renderedItemIds.has(id)) marks.set(id, 'settled');
    for (const id of pendingIds) if (renderedItemIds.has(id)) marks.set(id, 'pending');

    const prev = prevMarksRef.current;
    if (dequal(marks, prev)) return prev;

    const next = marks.size > 0 ? marks : EMPTY_SETTLE_MARKS;
    prevMarksRef.current = next;
    return next;
  }, [scope, enabled, renderedItemIds]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
